import { createLiveClock, type LiveClock, type SaleFeed } from '@auctisync/api-client';
import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * The server-corrected clock a countdown reads — a binding over the shared
 * `createLiveClock`, which decides whose clock that is (2026-10-08):
 *
 *   - a page with an engine feed → the engine's own clock, from the feed's
 *     `ack` (the engine closes the lots, so its clock is the one that counts);
 *   - otherwise → `get_server_time`, sampled every minute.
 *
 * Returns a STABLE function: countdowns call it every tick, and swapping the
 * clock underneath (the feed arriving a beat after the page) must not restart
 * their intervals. Before the first clock is up it reads the device's time.
 */
export function useLiveClock(feed: Pick<SaleFeed, 'serverOffsetMs'> | null): () => number {
  const clockRef = useRef<LiveClock | null>(null);

  useEffect(() => {
    const clock =
      feed !== null
        ? createLiveClock({ kind: 'engine', feed })
        : createLiveClock({ kind: 'supabase', supabase });
    clockRef.current = clock;
    return () => {
      clock.stop();
      if (clockRef.current === clock) clockRef.current = null;
    };
  }, [feed]);

  return useCallback(() => clockRef.current?.nowMs() ?? Date.now(), []);
}
